import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { DividerDashed, GridContainer, Section } from "../layout";
import { AnimatedSection, ResponsiveWidgetWrapper, staggerContainer, staggerItem } from "../shared";
import { PillarLabel } from "../ui";
import { DocumentsTableMiniWidget } from "../widgets/DocumentsTableMiniWidget";
import { InvestmentActivityLogWidget } from "../widgets/InvestmentActivityLogWidget";
import { InvestmentCardMiniWidget } from "../widgets/InvestmentCardMiniWidget";
import { MarketplaceEquilibriumWidget } from "../widgets/MarketplaceEquilibriumWidget";
import { QuotaProgressMiniWidget } from "../widgets/QuotaProgressMiniWidget";
import { useGridIntensityZone } from "../../hooks";

/**
 * Bento cell component - Linear-style with subtle borders
 * Clean design without hover effects
 */
function BentoCell({
  title,
  description,
  children,
  className = "",
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      variants={staggerItem}
      className={`relative p-5 flex flex-col overflow-hidden border border-border-primary rounded-lg ${className}`}
    >
      {/* Text overlay at top */}
      <div className="mb-4 flex-shrink-0">
        <h3 className="text-h3 text-text-tertiary mb-1">{title}</h3>
        <p className="text-body-small text-text-secondary max-w-[75%]">{description}</p>
      </div>
      {/* Widget content */}
      <div className="flex-1 flex items-center justify-center">{children}</div>
    </motion.div>
  );
}

/**
 * Pillar 3: Controla - Dashboard and monitoring features.
 * Bento Grid layout with subtle borders and 3D effects.
 */
export function PillarControla() {
  const sectionRef = useGridIntensityZone<HTMLElement>({ intensity: "subtle" });
  const gridRef = useRef<HTMLDivElement>(null);
  const isGridInView = useInView(gridRef, { once: true, margin: "-100px" });

  return (
    <Section ref={sectionRef} size="md" className="relative z-10">
      <AnimatedSection>
        <GridContainer>
          {/* Pillar Label */}
          <div className="col-span-4 md:col-span-8 xl:col-span-12">
            <PillarLabel number="03" label="Controla" />
          </div>

          {/* Header wrapped with dashed lines (touching) */}
          <div className="col-span-4 md:col-span-8 xl:col-span-12 mt-4">
            <DividerDashed />
            <h2 className="text-[28px] md:text-3xl xl:text-4xl font-semibold text-text-primary tracking-tight leading-tight">
              Todo en un sitio. <br className="md:hidden" />
              <span className="text-text-subtitle">Nada se escapa.</span>
            </h2>
            <DividerDashed />
          </div>
        </GridContainer>
      </AnimatedSection>

      {/* Bento Grid Layout with staggered animation */}
      <GridContainer className="mt-10">
        <div className="col-span-4 md:col-span-8 xl:col-span-12 h-auto">
          <motion.div
            ref={gridRef}
            className="flex flex-col gap-4"
            variants={staggerContainer}
            initial="hidden"
            animate={isGridInView ? "visible" : "hidden"}
          >
            {/* ROW 1-2: Two independent columns */}
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              {/* Left column: Activity Log + Estado documental */}
              <div className="flex-1 flex flex-col gap-4">
                <BentoCell
                  title="Trazabilidad total"
                  description="Cada acción queda registrada. Quién hizo qué y cuándo, listo para auditoría."
                  className="min-h-[350px] md:min-h-[450px] xl:min-h-[520px]"
                >
                  <div
                    className="translate-x-2 md:translate-x-8 xl:translate-x-12"
                    style={{
                      maskImage: "linear-gradient(to bottom, black 75%, transparent 100%)",
                      WebkitMaskImage: "linear-gradient(to bottom, black 75%, transparent 100%)",
                    }}
                  >
                    <InvestmentActivityLogWidget />
                  </div>
                </BentoCell>

                <BentoCell
                  title="Estado de inversión"
                  description="Monitoriza cada inversión con su estado al día. Fácil y ágil con badges visuales."
                  className="min-h-[220px] md:min-h-[250px] xl:min-h-[280px] pb-10"
                >
                  <div className="translate-x-4 md:translate-x-8 xl:translate-x-12">
                    <InvestmentCardMiniWidget />
                  </div>
                </BentoCell>
              </div>

              {/* Right column: Docs + Métricas stacked */}
              <div className="flex-1 flex flex-col gap-4">
                <div className="h-auto md:h-[400px] xl:h-[470px] flex-shrink-0">
                  <BentoCell
                    title="Documentación centralizada"
                    description="Docs de inversores, promotores e inversiones. Todo organizado, listo para consultar o descargar."
                    className="h-full"
                  >
                    <div
                      className="w-full flex justify-center translate-x-2 md:translate-x-6 xl:translate-x-8"
                      style={{
                        maskImage:
                          "linear-gradient(to right, black 0%, black 85%, transparent 100%)",
                        WebkitMaskImage:
                          "linear-gradient(to right, black 0%, black 85%, transparent 100%)",
                      }}
                    >
                      <ResponsiveWidgetWrapper designWidth={390} designHeight={280}>
                        <DocumentsTableMiniWidget
                          style={{
                            maskImage:
                              "linear-gradient(to bottom, black 0%, black 80%, transparent 100%)",
                            WebkitMaskImage:
                              "linear-gradient(to bottom, black 0%, black 80%, transparent 100%)",
                          }}
                        />
                      </ResponsiveWidgetWrapper>
                    </div>
                  </BentoCell>
                </div>

                <BentoCell
                  title="Métricas en tiempo real"
                  description="KPIs clave de tu operación: cupo, capacidad inversora, contratos y más. Todo de un vistazo."
                  className="min-h-[240px] md:min-h-[270px] xl:min-h-[300px] flex-shrink-0 pb-2 xl:pb-0"
                >
                  <div className="w-full flex justify-center translate-x-2 md:translate-x-6 xl:translate-x-8 -translate-y-1 md:-translate-y-2">
                    <ResponsiveWidgetWrapper
                      designWidth={420}
                      designHeight={200}
                      className="w-full max-w-[500px]"
                    >
                      <MarketplaceEquilibriumWidget
                        className="w-full"
                        style={{
                          maskImage:
                            "linear-gradient(to right, black 0%, black 75%, transparent 95%)",
                          WebkitMaskImage:
                            "linear-gradient(to right, black 0%, black 75%, transparent 95%)",
                        }}
                      />
                    </ResponsiveWidgetWrapper>
                  </div>
                </BentoCell>

                <BentoCell
                  title="Progreso del cupo"
                  description="Visualiza el estado del cupo de cada promotor. Objetivo, comprometido y disponible de un vistazo."
                  className="min-h-[220px] md:min-h-[250px] xl:min-h-[280px] pb-10"
                >
                  <QuotaProgressMiniWidget />
                </BentoCell>
              </div>
            </div>
          </motion.div>
        </div>
      </GridContainer>
    </Section>
  );
}
